import { css } from '@emotion/css';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  NumberFormatter,
  Stack,
  Text,
} from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import { debtorAtom } from 'app/Private/Clients/Detail/client-atom';
import { InfoRow } from 'components/InfoRow';
import { Panel } from 'components/Panel';
import { useCompanyConfiguration } from 'init/index';
import { useAtomValue } from 'jotai';
import { i18nKeys, tKeysLoose, useTranslation } from 'locales/index';
import { useUpdateClientTnc } from 'shared/hooks/use-update-client-tnc';
import { getCurrencySymbolFromCurrencyCode } from 'shared/utils/currency';
import { getCgv } from 'shared/utils/selectors';

export const stripedStack = css`
  & > *:nth-of-type(odd) {
    background-color: var(--mantine-color-purple-1);
    border-radius: 4px;
  }
  p {
    color: var(--mantine-color-gray-8);
  }
`;

export const TncPanel = () => {
  const { t } = useTranslation();
  const { updateClientTnc, isUpdateClientTncLoading } = useUpdateClientTnc();
  const company = useCompanyConfiguration();
  const { debtor, refetch } = useAtomValue(debtorAtom);

  const currentCgv = getCgv(company, debtor);

  const cgvItems = company.terms_and_conditions_attributes.map(
    ({ name, id, default: isDefault }) => ({
      description: name,
      value: Number(id),
      default: isDefault,
    }),
  );

  const handleUpdateTnc = (termsAndConditionsId: number) => {
    updateClientTnc(
      { id: String(debtor.id), data: { termsAndConditionsId } },
      { onSuccess: refetch },
    );
  };

  return (
    <Panel
      withBorder
      pb={'space5'}
      title={
        <Stack gap={0}>
          <Group>
            <Text>{t(i18nKeys.SALE_CONDITION)}</Text>
            {isUpdateClientTncLoading && <Loader size={20} />}
          </Group>
          {!currentCgv?.default && (
            <Text c="dimmed" size="xs">
              {t(i18nKeys.CLIENT.DETAIL.NOT_USING_DEFAULT_TNC)}
            </Text>
          )}
        </Stack>
      }
      topRight={
        <Menu position="bottom-end" width="auto" withinPortal radius="md">
          <Menu.Target>
            <Button
              variant="default"
              rightSection={<IconChevronDown size={18} stroke={1.5} />}
              pr={12}
              radius="md"
              size="xs"
            >
              {currentCgv?.name}
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {cgvItems.map(item => (
              <Menu.Item
                onClick={() => handleUpdateTnc(item.value)}
                bg={item.value === Number(currentCgv?.id) ? 'purple.1' : undefined}
                key={item.value}
              >
                {item.default && (
                  <Center inline>
                    <Text truncate="end">{item.description}</Text>
                    <Text ml="space1" c="purple.4">
                      · {t(i18nKeys.SALE_CONDITION_DEFAULT)}
                    </Text>
                  </Center>
                )}
                {!item.default && <Text truncate="end">{item.description}</Text>}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      }
    >
      <Stack gap={0} className={stripedStack}>
        <InfoRow label={t(i18nKeys.CLIENT.DETAIL.AVERAGE_DELAY_AUTHORIZED)} p={8}>
          <Text
            fw={500}
          >{`${currentCgv?.minimum_payment_terms_delay || '-'} ${t(i18nKeys.DAY)}`}</Text>
        </InfoRow>
        {currentCgv?.penalty_clause?.value && (
          <InfoRow
            label={t(i18nKeys.FORM.PREFERENCES.PENALTY_CLAUSE_VALUE, {
              text: currentCgv?.penalty_clause?.type === 'percentage' ? '%' : '€',
            })}
            p={8}
          >
            {currentCgv?.penalty_clause?.type === 'percentage' ? (
              <Group fw={500} gap="xs" c="gray.10">
                <NumberFormatter
                  value={currentCgv.penalty_clause.value}
                  decimalScale={2}
                  suffix=" %"
                />
                <Text>-</Text>
                <NumberFormatter
                  value={currentCgv.penalty_clause.minimum}
                  decimalScale={2}
                  suffix={` ${getCurrencySymbolFromCurrencyCode(debtor.currency)}`}
                />
              </Group>
            ) : (
              <Box fw={500}>
                <NumberFormatter
                  value={currentCgv?.penalty_clause?.value}
                  decimalScale={2}
                  suffix={` ${getCurrencySymbolFromCurrencyCode(debtor.currency)}`}
                />
              </Box>
            )}
          </InfoRow>
        )}
        {currentCgv?.interest?.value && (
          <InfoRow label={t(i18nKeys.FORM.PREFERENCES.INTEREST)} p={8}>
            <Group gap="xs">
              <Text fw={500}>{currentCgv.interest.value} %</Text>
              <Text>-</Text>
              <Text fw={500}>
                {t(tKeysLoose.CALCULATION_TYPE[currentCgv?.interest?.period.toUpperCase()])}
              </Text>
            </Group>
          </InfoRow>
        )}
        {currentCgv?.fix_fee?.value && (
          <InfoRow label={t(i18nKeys.FORM.PREFERENCES.FIX_FEE)} p={8}>
            <Text fw={500}>
              <NumberFormatter
                value={currentCgv.fix_fee.value}
                decimalScale={2}
                suffix={` ${getCurrencySymbolFromCurrencyCode(debtor.currency)}`}
              />
            </Text>
          </InfoRow>
        )}
      </Stack>
    </Panel>
  );
};
